import { useParams, Link, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  getEtsyConfigSetById,
  deleteEtsyConfigSet,
  getProductsByConfigSet,
} from '@/lib/queries/configSets'
import { useState } from 'react'
import { IoPencilSharp } from 'react-icons/io5'
import toast from 'react-hot-toast'

/**
 * Etsy Config Set Detail page
 * Shows detailed information about an Etsy config set
 */
export default function EtsyConfigSetDetail() {
  const { id, brandId } = useParams<{ id: string; brandId?: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [showMenu, setShowMenu] = useState(false)
  const etsyBasePath = brandId ? `/brands/${brandId}/integrations/etsy` : '/integrations/etsy'

  const { data: configSet, isLoading, error } = useQuery({
    queryKey: ['etsyConfigSet', id],
    queryFn: () => getEtsyConfigSetById(id!),
    enabled: !!id,
    staleTime: 0,
    gcTime: 0,
  })

  const { data: linkedProducts = [], isLoading: isLoadingProducts, error: productsError } = useQuery({
    queryKey: ['productsByConfigSet', id],
    queryFn: () => getProductsByConfigSet(id!),
    enabled: !!id,
    staleTime: 0,
    gcTime: 0,
  })

  // Debug: Log linked products
  if (linkedProducts.length > 0) {
    console.log(`[Config Set Detail] Found ${linkedProducts.length} linked products:`, linkedProducts.map(p => ({ id: p._id, title: p.title, sku: p.sku })))
  } else if (!isLoadingProducts && !productsError) {
    console.log(`[Config Set Detail] No linked products found for config set ${id}`)
  }

  const { mutateAsync: doDelete, isPending: isDeleting } = useMutation({
    mutationFn: deleteEtsyConfigSet,
    onSuccess: async () => {
      if (!id) return
      toast.success('Config set deleted successfully')
      // Remove the deleted config set from cache
      queryClient.removeQueries({ queryKey: ['etsyConfigSet', id] })
      queryClient.removeQueries({ queryKey: ['productsByConfigSet', id] })
      // Remove list from cache and refetch fresh data
      queryClient.removeQueries({ queryKey: ['etsyConfigSets'] })
      await queryClient.refetchQueries({ queryKey: ['etsyConfigSets'] })
      navigate(etsyBasePath)
    },
  })

  const handleDelete = async () => {
    if (!id || !configSet) return
    const displayName = configSet.name || 'this config set'
    const ok = window.confirm(
      `Are you sure you want to delete "${displayName}"? This cannot be undone.`
    )
    if (!ok) return
    setShowMenu(false)
    try {
      await doDelete(id)
    } catch (error) {
      toast.error(`Failed to delete config set: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  // Helper to format date (UK format: DD MM YYYY)
  const formatDate = (dateString: string | undefined): string => {
    if (!dateString) return 'N/A'
    try {
      const date = new Date(dateString)
      const day = String(date.getDate()).padStart(2, '0')
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const year = date.getFullYear()
      return `${day} ${month} ${year}`
    } catch {
      return 'N/A'
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 pb-24">
        <header className="sticky top-0 bg-white border-b border-gray-200 z-10">
          <div className="p-4">
            <h1 className="text-2xl font-bold text-gray-900">Loading config set...</h1>
          </div>
        </header>
      </div>
    )
  }

  if (error || !configSet) {
    return (
      <div className="min-h-screen bg-gray-50 pb-24">
        <header className="sticky top-0 bg-white border-b border-gray-200 z-10">
          <div className="p-4">
            <h1 className="text-2xl font-bold text-gray-900">Config Set Not Found</h1>
          </div>
        </header>
        <main className="p-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800">
              {error instanceof Error ? error.message : 'Config set not found'}
            </p>
            <Link
              to={etsyBasePath}
              className="mt-4 inline-block text-blue-600 hover:underline font-medium"
            >
              ← Back to Config Sets
            </Link>
          </div>
        </main>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      {/* Header */}
      <header className="sticky top-0 bg-white border-b border-gray-200 z-10">
        <div className="p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link
              to={etsyBasePath}
              className="text-gray-600 hover:text-gray-900"
              aria-label="Back to config sets"
            >
              ←
            </Link>
            <h1 className="text-2xl font-bold text-gray-900">Config Set Detail</h1>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to={`${etsyBasePath}/${id}/edit`}
              className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center touch-target-min-44 hover:bg-gray-200 transition-colors"
              aria-label="Edit"
            >
              <IoPencilSharp className="w-6 h-6 text-gray-800" />
            </Link>
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowMenu(!showMenu)}
                className="p-2 text-gray-600 hover:text-gray-900 touch-target-min-44"
                aria-label="More options"
              >
                ⋯
              </button>
              {showMenu && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setShowMenu(false)}
                  />
                  <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-20">
                    <button
                      type="button"
                      onClick={handleDelete}
                      disabled={isDeleting}
                      className="w-full text-left px-4 py-3 text-red-600 hover:bg-red-50 rounded-lg font-medium touch-target-min-44 disabled:opacity-50"
                    >
                      {isDeleting ? 'Deleting...' : 'Delete Config Set'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="p-4 space-y-4">
        {/* Basic Information */}
        <section className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h2>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium text-gray-700">Name</label>
              <p className="mt-1 text-gray-900">{configSet.name || 'N/A'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Description</label>
              <p className="mt-1 text-gray-900 whitespace-pre-wrap">
                {configSet.description || 'No description'}
              </p>
            </div>
            {configSet.originListingId && (
              <div>
                <label className="text-sm font-medium text-gray-700">Origin Listing ID</label>
                <p className="mt-1 text-gray-900">{configSet.originListingId}</p>
              </div>
            )}
          </div>
        </section>

        {/* Etsy Configuration */}
        <section className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Etsy Configuration</h2>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium text-gray-700">Shipping Template ID</label>
              <p className="mt-1 text-gray-900">{configSet.shipping_template_id || 'N/A'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Taxonomy ID</label>
              <p className="mt-1 text-gray-900">{configSet.taxonomy_id || 'N/A'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Shop Section ID</label>
              <p className="mt-1 text-gray-900">{configSet.shop_section_id || 'N/A'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Return Policy ID</label>
              <p className="mt-1 text-gray-900">{configSet.return_policy_id || 'N/A'}</p>
            </div>
            {configSet.currency_code && (
              <div>
                <label className="text-sm font-medium text-gray-700">Currency</label>
                <p className="mt-1 text-gray-900">
                  {configSet.currency_code.code || configSet.currency_code.name || 'N/A'}
                </p>
              </div>
            )}
          </div>
        </section>

        {/* Product Details */}
        <section className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Product Details</h2>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium text-gray-700">Who Made</label>
              <p className="mt-1 text-gray-900">{configSet.who_made || 'N/A'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">When Made</label>
              <p className="mt-1 text-gray-900">{configSet.when_made || 'N/A'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Is Supply</label>
              <p className="mt-1 text-gray-900">{configSet.is_supply ? 'Yes' : 'No'}</p>
            </div>
            {configSet.processing_min !== undefined && configSet.processing_max !== undefined && (
              <div>
                <label className="text-sm font-medium text-gray-700">Processing Time</label>
                <p className="mt-1 text-gray-900">
                  {configSet.processing_min} - {configSet.processing_max} days
                </p>
              </div>
            )}
          </div>
        </section>

        {/* Materials & Tags */}
        {(configSet.materials?.length || configSet.tags?.length) && (
          <section className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Materials & Tags</h2>
            <div className="space-y-3">
              {configSet.materials && configSet.materials.length > 0 && (
                <div>
                  <label className="text-sm font-medium text-gray-700">Materials</label>
                  <div className="mt-1 flex flex-wrap gap-2">
                    {configSet.materials.map((material) => (
                      <span
                        key={material._id}
                        className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-sm"
                      >
                        {material.name || material._ref}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {configSet.tags && configSet.tags.length > 0 && (
                <div>
                  <label className="text-sm font-medium text-gray-700">Tags</label>
                  <div className="mt-1 flex flex-wrap gap-2">
                    {configSet.tags.map((tag) => (
                      <span
                        key={tag._id}
                        className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-sm"
                      >
                        {tag.name || tag._ref}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </section>
        )}

        {/* Linked Products */}
        <section className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Linked Products ({linkedProducts.length})
          </h2>
          {isLoadingProducts ? (
            <div className="text-sm text-gray-500">Loading products...</div>
          ) : productsError ? (
            <div className="text-sm text-red-600">
              Error loading products: {productsError instanceof Error ? productsError.message : 'Unknown error'}
            </div>
          ) : linkedProducts.length === 0 ? (
            <div className="text-sm text-gray-500">
              No products are using this config set.
              {id && (
                <div className="mt-2 text-xs text-gray-400">
                  Config Set ID: {id}
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              {linkedProducts.map((product) => {
                const isDraft = product._id.startsWith('drafts.')
                // Find which channels reference this config set
                const linkedChannels = product.channelMapping
                  ?.filter((mapping) => mapping.configSet?._ref === id)
                  .map((mapping) => mapping.channel)
                  .filter(Boolean) || []

                return (
                  <Link
                    key={product._id}
                    to={`/products/${product._id}`}
                    className="block p-3 border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-gray-300 transition-colors touch-target-min-44"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <h3 className="font-medium text-gray-900 truncate">
                            {product.title || 'Untitled Product'}
                          </h3>
                          {isDraft && (
                            <span className="px-2 py-0.5 bg-orange-100 text-orange-800 rounded text-xs font-medium">
                              Draft
                            </span>
                          )}
                        </div>
                        {product.sku && (
                          <p className="text-sm text-gray-600 mt-1">SKU: {product.sku}</p>
                        )}
                        <p className="text-xs text-gray-500 mt-1">ID: {product._id}</p>
                        {linkedChannels.length > 0 && (
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-xs text-gray-600">Linked via:</span>
                            {linkedChannels.map((channel) => (
                              <span
                                key={channel}
                                className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded text-xs font-medium capitalize"
                              >
                                {channel}
                              </span>
                            ))}
                          </div>
                        )}
                        <div className="flex items-center gap-2 mt-2">
                          {product.rrp !== undefined && (
                            <span className="text-sm font-semibold text-blue-600">
                              £{product.rrp.toFixed(2)}
                            </span>
                          )}
                          {product.isPrintOnDemand ? (
                            <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded text-xs font-medium">
                              POD
                            </span>
                          ) : (
                            <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-medium">
                              Physical
                            </span>
                          )}
                        </div>
                      </div>
                      <span className="text-gray-400">→</span>
                    </div>
                  </Link>
                )
              })}
            </div>
          )}
        </section>

        {/* Metadata */}
        <section className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Metadata</h2>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium text-gray-700">Created</label>
              <p className="mt-1 text-gray-900">{formatDate(configSet._createdAt)}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Last Updated</label>
              <p className="mt-1 text-gray-900">{formatDate(configSet._updatedAt)}</p>
            </div>
          </div>
        </section>

        {/* Actions */}
        <div className="flex gap-3">
          <Link
            to={etsyBasePath}
            className="flex-1 bg-gray-100 text-gray-900 py-3 px-6 rounded-lg font-medium text-center touch-target-min-44 hover:bg-gray-200 transition-colors"
          >
            Back to List
          </Link>
        </div>
      </main>
    </div>
  )
}
